import type { ConfidenceBand, PendingFinding } from './types';

export const CONFIDENCE_BANDS = { high: 0.9, medium: 0.7 } as const;

export const confidenceBand = (confidence: number | null): ConfidenceBand => {
  if (confidence == null) return 'unknown';
  if (confidence >= CONFIDENCE_BANDS.high) return 'high';
  if (confidence >= CONFIDENCE_BANDS.medium) return 'medium';
  return 'low';
};

export interface FindingsGroupData {
  key: ConfidenceBand;
  title: string;
  findings: PendingFinding[];
}

const BAND_ORDER: ConfidenceBand[] = ['unknown', 'low', 'medium', 'high'];

const BAND_TITLES: Record<ConfidenceBand, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
  unknown: 'Unscored',
};

const RESOURCE_TYPE_ORDER = ['Condition', 'Observation', 'Goal'];

const CATEGORY_LABELS: Record<string, string> = {
  Condition: 'Conditions',
  Observation: 'Observations',
  Goal: 'Goals',
};

const byConfidenceDesc = (a: PendingFinding, b: PendingFinding) =>
  (b.confidence ?? -1) - (a.confidence ?? -1);

const typeRank = (resourceType: string) => {
  const rank = RESOURCE_TYPE_ORDER.indexOf(resourceType);
  return rank === -1 ? RESOURCE_TYPE_ORDER.length : rank;
};

interface FindingsCategory {
  key: string;
  label: string;
  findings: PendingFinding[];
}

export const categorize = (findings: PendingFinding[]): FindingsCategory[] => {
  const types = [...new Set(findings.map((f) => f.resourceType))].sort(
    (a, b) => typeRank(a) - typeRank(b) || a.localeCompare(b),
  );
  return types.map((resourceType) => ({
    key: resourceType,
    label: CATEGORY_LABELS[resourceType] ?? resourceType,
    findings: findings
      .filter((f) => f.resourceType === resourceType)
      .sort(byConfidenceDesc),
  }));
};

export const groupFindings = (
  findings: PendingFinding[],
): FindingsGroupData[] =>
  BAND_ORDER.map((band) => ({
    key: band,
    title: BAND_TITLES[band],
    findings: findings
      .filter((f) => confidenceBand(f.confidence) === band)
      .sort(byConfidenceDesc),
  })).filter((group) => group.findings.length > 0);
